import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ProductKey } from '@/router/products'
import {
  useSubscription,
  formatEur,
} from '@/composables/useSubscription'
import { useSubscriptionState } from '@/composables/useSubscriptionState'
import { useSubscriptionFlow } from '@/composables/useSubscriptionFlow'
import { subscriptionFlowFor } from '@/subscriptionFlows'
import { addMonthsInclusive } from '@/subscriptionFlows/SubscriptionFlowDefinition'
import { formatStayDuration } from '@/subscriptionFlows/pmeBilling'
import type { AgeBounds } from '@/utils/birthDate'

/**
 * Vue-models de l'offre retenue + du récapitulatif de finalisation, partagés
 * entre les écrans « offre sur-mesure » de SubscriptionStartCard et le récap
 * final d'InfoForm. Centralise les règles métier communes (offre choisie, dates
 * de contrat, sous-étapes infos, totaux, libellés) pour qu'elles vivent en un
 * seul endroit. Pas d'état propre : tout dérive des composables singletons
 * (useSubscription / useSubscriptionFlow) et de l'URL (useSubscriptionState).
 */

/** Illustration par défaut des packs « Santé + assurance ». */
const PACK_IMAGE = '/products/pack-sante-assurance.webp'
/** Illustration commune à toutes les formules « Complémentaire Santé ». */
const PRODUCT_IMAGE = '/products/complementaire-sante.webp'
/** Illustration Tonic-Études (mêmes dimensions que complementaire-sante.webp). */
const TOP_UP_IMAGE = '/products/tonic-etudes.webp'
/** Illustration du Club Smerra Stud'Life pour le récap final (réassurance). */
const CLUB_IMAGE = '/products/club-stud-life.webp'
/** Illustrations mutuelle AURA (à déposer dans /public/products/). */
const REGIONAL_IMAGES: Record<string, string> = {
  primordiale: '/products/aura-primordiale.webp',
  renforcee: '/products/aura-renforcee.webp',
}
/** Illustrations offres responsabilité civile pour la carte du récap final. */
const CIVIL_IMAGES: Record<string, string> = {
  security: '/products/assurance-vie-etudiante-securite.webp',
  indispensable: '/products/assurance-vie-etudiante-indispensable.webp',
}
/** Illustrations packs étranger / « en France » pour les cartes du récap final. */
const ABROAD_IMAGES: Record<string, string> = {
  packEurope: '/products/pack-europe.webp',
  packMonde: '/products/pack-monde.webp',
}
const FRANCE_IMAGES: Record<string, string> = {
  packFranceGlobal: '/products/pack-en-france-global.webp',
  packFranceConfort: '/products/pack-en-france-confort.webp',
}
/** Illustration Solidarité Exam pour les cartes résultats / récap final. */
const EXAM_IMAGE = '/products/solidarite-exam.webp'
/** Minimum subscribed months for the monthly payment option to be offered;
 *  below it (a single month) the cover is paid upfront. */
const MIN_MONTHS_FOR_MONTHLY = 2

// `pricePerYear` : tarif annuel standard (écran « offre sur-mesure »).
// `periodTotal` : prix de CE produit pour la période choisie (récap final, back-end).
// `periodMonthly` : tarif mensuel standard de CE produit (paiement mensuel).
export type SummaryCard = {
  image: string
  alt: string
  category: string
  name: string
  pricePerYear: number | null
  periodTotal: number | null
  periodMonthly: number | null
}

/** A recap-card stripped to what the generic `RecapStep` renders (prices are
 *  numbers; the component words them per the payment mode + the flow's priceMode). */
export type RecapViewCard = {
  image: string
  alt: string
  category: string
  name: string
  periodTotal: number | null
  periodMonthly: number | null
}

/** A contract-dates row (start / end / tacit renewal), already localized. */
export type RecapDateRow = { label: string; value: string }

/** The fully-resolved view-model the generic final-recap screen renders. Built
 *  only for flow-wired products; null otherwise (those keep their own block). */
export type RecapView = {
  cards: RecapViewCard[]
  dateRows: RecapDateRow[]
  showPaymentSelector: boolean
  /** Whether the monthly payment option is offered: only for duration covers of
   *  at least `MIN_MONTHS_FOR_MONTHLY` months (a 1-month cover is paid upfront). */
  monthlyAvailable: boolean
  showClub: boolean
  clubImage: string
  priceMode: 'duration' | 'stay'
  /** Precomputed "X € for <stay>" text (priceMode 'stay'). */
  stayText: string
  /** Subscribed months (priceMode 'duration'), for the "for N months" wording. */
  durationCount: number | null
  total: number | null
  totalMonthly: number | null
}

export function useSubscriptionOffer(productKey: MaybeRefOrGetter<ProductKey | undefined>) {
  const { t } = useI18n()
  const { answers, locale } = useSubscriptionState()
  const {
    startDate,
    duration,
    stayEnd,
    stayDays,
    billedMonths,
    subscriptionTotal: subTotal,
    monthlyTotal: subMonthlyTotal,
    yearlyTotal: subYearlyTotal,
  } = useSubscription()
  const {
    crossSellAddons: healthCrossSellAddons,
    selectedPlanPrice: healthPlanPrice,
    selectedBundleKey: healthSelectedBundleKey,
    assiaForwardingAutomatic,
    tonicPrice: healthTonicPrice,
    tonicLabelKey: healthTonicLabelKey,
    monthlyTotal: healthMonthlyTotal,
    yearlyTotal: healthYearlyTotal,
    subscriptionTotal: healthSubscriptionTotal,
    planPeriodTotal: healthPlanPeriodTotal,
    tonicPeriodTotal: healthTonicPeriodTotal,
  } = useSubscriptionFlow()

  const isHealthExpenses = computed(() => toValue(productKey) === 'healthExpenses')
  const isCivil = computed(() => toValue(productKey) === 'civil')
  const isAbroad = computed(() => toValue(productKey) === 'abroad')
  const isArrivingFrance = computed(() => toValue(productKey) === 'arrivingFrance')
  const isHealthTopUp = computed(() => toValue(productKey) === 'healthTopUp')
  const isRegionalAura = computed(() => toValue(productKey) === 'regionalAura')
  const isSolidariteExam = computed(() => toValue(productKey) === 'solidariteExam')

  /** Borne d'âge supérieure du parcours : 34 ans pour l'étranger, 30 ailleurs. */
  const maxSubscriptionAge = computed(() => (isAbroad.value ? 34 : 30))

  /** Âge minimum d'un enfant (borne basse de la tranche « intermédiaire ») :
   *  18 ans pour la responsabilité civile (réservée aux majeurs), 15 ans ailleurs. */
  const minChildAge = computed(() => (isCivil.value ? 18 : 15))

  /**
   * Age limits of the current journey, resolved from the product and the audience.
   * A subscriber signing for themselves must be of age (18); a parent subscribing
   * for their child only has to clear the product's own floor.
   *
   * Single source of truth for every age guard: the decision tree's bracket labels
   * ("Entre {min} et {max} ans"), the birth-date year list and the birth-date
   * validation all read it, so a bound can never be raised in one place only.
   */
  const ageBounds = computed<AgeBounds>(() => ({
    min: answers.value.forWhom === 'child' ? minChildAge.value : 18,
    max: maxSubscriptionAge.value,
  }))

  /** Prix € : 2 décimales seulement s'il y a des centimes (5 € / 25,50 €). */
  function formatPrice(value: number): string {
    return formatEur(value, locale.value)
  }
  function formatDate(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0')
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`
  }

  /** Nom affiché d'une offre détaillée (titre du « En savoir + »). */
  function detailName(key: string): string {
    if (isCivil.value) return t(`subscription.civilResults.products.${key}.name`)
    if (isAbroad.value) return t(`subscription.abroadResults.products.${key}.name`)
    if (isArrivingFrance.value) return t(`subscription.franceResults.products.${key}.name`)
    if (isHealthTopUp.value) return t('subscription.topUp.name')
    if (isRegionalAura.value) return t(`subscription.regionalResults.products.${key}.name`)
    if (isSolidariteExam.value) return t('subscription.examResults.name')
    return t(`subscription.results.tiers.${key}`)
  }

  /** Nombre de sous-étapes « Vos informations » : 4 quand le bundle choisi collecte
   *  les champs Assia (identité, adresse, e-mail, RIB), sinon 3 (l'étape RIB est
   *  omise). Le récap final est juste après. */
  const infoStepCount = computed(() => (assiaForwardingAutomatic.value ? 4 : 3))
  const infoRecapStep = computed(() => infoStepCount.value + 1)

  const hasAddon = computed(
    () => answers.value.crossSell === 'security' || answers.value.crossSell === 'indispensable',
  )
  const tonicSubscribed = computed(() => answers.value.topUp === 'subscribe')

  /** Nom Tonic-Études : issu du bundle back-end (`bundles.<key>`) dans le parcours
   *  santé, repli sur le libellé en dur tant que le bundle n'est pas chargé. */
  const topUpName = computed(() =>
    healthTonicLabelKey.value ? t(`bundles.${healthTonicLabelKey.value}`) : t('subscription.topUp.name'),
  )

  /* --- Offre / dates de contrat par produit ---------------------------- */

  /** Date de fin = date de début + durée (en mois). */
  const endDate = computed<Date | null>(() => {
    if (!startDate.value || !duration.value) return null
    const s = startDate.value
    return new Date(s.getFullYear(), s.getMonth() + duration.value, s.getDate())
  })

  /** RC : couverture fixe d'un an (tarif annuel, tacite reconduction) → fin de
   *  contrat = veille de l'anniversaire (ex. 26/06/2026 → 25/06/2027). */
  const civilEndDate = computed<Date | null>(() => {
    if (!startDate.value) return null
    const s = startDate.value
    return new Date(s.getFullYear() + 1, s.getMonth(), s.getDate() - 1)
  })

  /** En France : fin de contrat = dernier jour couvert de la période (début +
   *  durée mois − 1 jour, avec clamp de fin de mois). Identique à l'`effectiveUntil`
   *  envoyé au back-end, via le même helper que le flow. */
  const franceEndDate = computed<Date | null>(() => {
    if (!startDate.value || !duration.value) return null
    return addMonthsInclusive(startDate.value, duration.value)
  })

  /** Pack étranger retenu (étape « plan » répondue) → carte « Votre offre ».
   *  Le tarif mensuel vient du bundle back-end (price_international via le flow) ;
   *  null tant que le bundle n'est pas chargé. */
  const abroadChosen = computed(() => {
    const key = answers.value.plan
    if (!isAbroad.value || !key) return null
    return { key, pricePerMonth: healthPlanPrice.value?.perMonth ?? null }
  })

  /** Offre RC retenue (étape « plan » répondue) → carte « Votre offre ». Le tarif
   *  annuel vient du bundle back-end (via le flow) ; null tant que le bundle n'est
   *  pas chargé. */
  const civilChosen = computed(() => {
    const key = answers.value.plan
    if (!isCivil.value || !key) return null
    return { key, pricePerYear: healthPlanPrice.value?.perYear ?? null }
  })

  /** Pack « en France » retenu (étape « plan » répondue) → carte « Votre offre ».
   *  Le tarif mensuel vient du bundle back-end (via le flow) ; null tant que le
   *  bundle n'est pas chargé. */
  const franceChosen = computed(() => {
    const key = answers.value.plan
    if (!isArrivingFrance.value || !key) return null
    return { key, pricePerMonth: healthPlanPrice.value?.perMonth ?? null }
  })

  /* --- Totaux : back-end pour les produits branchés (santé, RC, étranger, France,
   *  surcomplémentaire, mutuelle AURA), estimation front en repli. ------------- */
  const usesBackendPricing = computed(
    () =>
      isHealthExpenses.value ||
      isCivil.value ||
      isAbroad.value ||
      isArrivingFrance.value ||
      isHealthTopUp.value ||
      isRegionalAura.value ||
      isSolidariteExam.value,
  )
  const subscriptionTotal = computed(() =>
    usesBackendPricing.value ? healthSubscriptionTotal.value : subTotal.value,
  )
  const monthlyTotal = computed(() =>
    usesBackendPricing.value ? healthMonthlyTotal.value : subMonthlyTotal.value,
  )
  const yearlyTotal = computed(() =>
    usesBackendPricing.value ? healthYearlyTotal.value : subYearlyTotal.value,
  )

  /** Stay length for display (e.g. « 70 jours (2,5 mois facturés) »). The wording and its
   *  pluralisation rules live in formatStayDuration, next to the billing maths they describe
   *  and shared with the quote recap. */
  const stayDurationText = computed(() =>
    formatStayDuration(stayDays.value, billedMonths.value, t, locale.value),
  )

  /** « {total} pour {durée} » pour le récap étranger (ex. « 19 € pour 15 jours »). */
  const stayTotalText = computed(() =>
    subscriptionTotal.value == null
      ? ''
      : t('subscription.info.finalRecap.priceForStay', {
          price: formatPrice(subscriptionTotal.value),
          duration: stayDurationText.value,
        }),
  )

  /** Encart contextualisé Tonic : nom selon l'ajout d'une assurance. */
  const topUpOfferName = computed(() =>
    hasAddon.value ? t('subscription.topUp.packName') : t('subscription.topUp.name'),
  )

  /** Nom de l'offre surcomplémentaire pour les écrans récap (« Votre offre » et
   *  « Récapitulatif ») : libellé du bundle retenu (Tonic-Études, ou combiné
   *  « Tonic-Études + Sécurité/Indispensable ») issu du back-end via la clé
   *  `bundles`, avec repli sur le nom marketing tant que le bundle n'est pas chargé. */
  const topUpBundleName = computed(() =>
    healthSelectedBundleKey.value
      ? t(`bundles.${healthSelectedBundleKey.value}`)
      : topUpOfferName.value,
  )

  /** Nom de l'offre AURA pour les écrans récap (« Votre offre » et « Récapitulatif ») :
   *  libellé du bundle retenu (formule seule, ou combiné « formule + Sécurité/
   *  Indispensable ») issu du back-end via la clé `bundles`, avec repli sur le nom
   *  marketing de la formule tant que le bundle n'est pas chargé. */
  const regionalOfferName = computed(() => {
    if (healthSelectedBundleKey.value) return t(`bundles.${healthSelectedBundleKey.value}`)
    const plan = answers.value.plan
    return plan ? t(`subscription.regionalResults.products.${plan}.name`) : ''
  })
  /** Visuel de l'encart AURA : la formule choisie. */
  const regionalOfferImage = computed(() => REGIONAL_IMAGES[answers.value.plan] ?? REGIONAL_IMAGES.primordiale)

  /** Nom de l'offre Solidarité Exam pour les écrans récap : libellé du bundle
   *  retenu issu du back-end (clé `bundles`), repli sur le nom local tant que le
   *  bundle n'est pas chargé. */
  const examOfferName = computed(() =>
    healthSelectedBundleKey.value
      ? t(`bundles.${healthSelectedBundleKey.value}`)
      : t('subscription.examResults.name'),
  )

  /** Cartes du récapitulatif : pack (santé + assurance) ou complémentaire seule,
   *  + une carte surcomplémentaire si Tonic-Études a été souscrite. Tarifs annuels
   *  issus du back-end ; null tant que les bundles ne sont pas chargés (masqué). */
  const summaryCards = computed<SummaryCard[]>(() => {
    const cards: SummaryCard[] = []
    const tier = answers.value.plan
    const planPrice = healthPlanPrice.value?.perYear ?? null
    const planMonthly = healthPlanPrice.value?.perMonth ?? null
    // Label du bundle choisi (formule, ou « formule + add-on » si pack) issu du
    // back-end ; repli sur les libellés en dur tant que le bundle n'est pas chargé.
    const bundleName = healthSelectedBundleKey.value ? t(`bundles.${healthSelectedBundleKey.value}`) : null
    if (hasAddon.value) {
      // Pack = formule + assurance : on additionne le tarif de l'add-on choisi.
      const addon = healthCrossSellAddons.value.find((a) => a.key === answers.value.crossSell)
      const addonPrice = addon?.pricePerYear ?? null
      const addonMonthly = addon?.pricePerMonth ?? null
      cards.push({
        image: PACK_IMAGE,
        alt: t('subscription.summary.packAlt'),
        category: t('subscription.summary.packCategory'),
        name: bundleName ?? t('subscription.summary.packName'),
        pricePerYear: planPrice !== null && addonPrice !== null ? planPrice + addonPrice : null,
        // Le prix période de la formule (back-end) inclut déjà l'add-on (bundle combiné).
        periodTotal: healthPlanPeriodTotal.value,
        periodMonthly: planMonthly !== null && addonMonthly !== null ? planMonthly + addonMonthly : null,
      })
    } else {
      cards.push({
        image: PRODUCT_IMAGE,
        alt: t('subscription.results.imageAlt'),
        category: t('subscription.results.category'),
        name: bundleName ?? t(`subscription.results.tiers.${tier}`),
        pricePerYear: planPrice,
        periodTotal: healthPlanPeriodTotal.value,
        periodMonthly: planMonthly,
      })
    }
    if (tonicSubscribed.value) {
      cards.push({
        image: TOP_UP_IMAGE,
        alt: t('subscription.topUp.imageAlt'),
        category: t('subscription.topUp.category'),
        name: topUpName.value,
        pricePerYear: healthTonicPrice.value?.perYear ?? null,
        periodTotal: healthTonicPeriodTotal.value,
        periodMonthly: healthTonicPrice.value?.perMonth ?? null,
      })
    }
    return cards
  })

  /** Generic final-recap view-model for flow-wired products (health, abroad,
   *  France); null for the others, which keep their own recap block. Reads the
   *  flow's static `recap` policy and resolves the reactive cards/dates/totals. */
  const recapView = computed<RecapView | null>(() => {
    const flow = subscriptionFlowFor(toValue(productKey))
    if (!flow) return null
    const policy = flow.recap

    // Cards: health may show two (formula + top-up); abroad/France show one.
    let cards: RecapViewCard[] = []
    if (isHealthExpenses.value) {
      cards = summaryCards.value.map((c) => ({
        image: c.image,
        alt: c.alt,
        category: c.category,
        name: c.name,
        periodTotal: c.periodTotal,
        periodMonthly: c.periodMonthly,
      }))
    } else if (isCivil.value && civilChosen.value) {
      const key = civilChosen.value.key
      cards = [{
        image: CIVIL_IMAGES[key] ?? '',
        alt: detailName(key),
        category: t('subscription.civilResults.category'),
        name: detailName(key),
        periodTotal: subscriptionTotal.value,
        periodMonthly: null,
      }]
    } else if (isAbroad.value && abroadChosen.value) {
      const key = abroadChosen.value.key
      cards = [{
        image: ABROAD_IMAGES[key] ?? '',
        alt: detailName(key),
        category: t('subscription.abroadResults.category'),
        name: detailName(key),
        periodTotal: subscriptionTotal.value,
        periodMonthly: null,
      }]
    } else if (isArrivingFrance.value && franceChosen.value) {
      const key = franceChosen.value.key
      cards = [{
        image: FRANCE_IMAGES[key] ?? '',
        alt: detailName(key),
        category: t('subscription.franceResults.category'),
        name: detailName(key),
        periodTotal: subscriptionTotal.value,
        periodMonthly: monthlyTotal.value,
      }]
    } else if (isHealthTopUp.value && answers.value.plan) {
      // Une seule carte : Tonic-Études, ou le bundle combiné « Tonic + assurance »
      // quand un add-on est retenu (l'add-on est fondu dans ce bundle côté back-end).
      cards = [{
        image: TOP_UP_IMAGE,
        alt: topUpBundleName.value,
        category: t('subscription.topUp.category'),
        name: topUpBundleName.value,
        periodTotal: subscriptionTotal.value,
        periodMonthly: monthlyTotal.value,
      }]
    } else if (isRegionalAura.value && answers.value.plan) {
      // Une seule carte : la formule (Primordiale/Renforcée), ou le bundle combiné
      // « formule + Sécurité/Indispensable » quand un add-on est retenu (fondu dans
      // ce bundle côté back-end). Visuel par formule.
      cards = [{
        image: regionalOfferImage.value,
        alt: regionalOfferName.value,
        category: t('subscription.regionalResults.category'),
        name: regionalOfferName.value,
        periodTotal: subscriptionTotal.value,
        periodMonthly: monthlyTotal.value,
      }]
    } else if (isSolidariteExam.value && answers.value.plan) {
      // Une seule carte : le bundle Solidarité exam. Nom issu du back-end
      // (bundles.<key>), repli sur le libellé local tant qu'il n'est pas chargé.
      cards = [{
        image: EXAM_IMAGE,
        alt: examOfferName.value,
        category: t('subscription.examResults.category'),
        name: examOfferName.value,
        periodTotal: subscriptionTotal.value,
        periodMonthly: monthlyTotal.value,
      }]
    }

    // Contract end date: each flow keeps its own (stay end, civil/top-up/AURA
    // one-year anniversary, France last covered day, or start + duration).
    const end = isAbroad.value
      ? stayEnd.value
      : isArrivingFrance.value
        ? franceEndDate.value
        : isCivil.value || isHealthTopUp.value || isRegionalAura.value || isSolidariteExam.value
          ? civilEndDate.value
          : endDate.value

    const dateRows: RecapDateRow[] = []
    if (startDate.value && end) {
      dateRows.push({ label: t('subscription.info.finalRecap.startContract'), value: formatDate(startDate.value) })
      dateRows.push({ label: t('subscription.info.finalRecap.endContract'), value: formatDate(end) })
      if (policy.tacitRow !== 'hidden') {
        dateRows.push({
          label: t('subscription.info.finalRecap.tacitRenewal'),
          value: t(policy.tacitRow === 'yes'
            ? 'subscription.info.finalRecap.tacitYes'
            : 'subscription.info.finalRecap.tacitNo'),
        })
      }
      // Pack (formule + assurance) : l'Assurance Examen est offerte avec l'add-on.
      if (flow.crossSell?.bundlesExamInsurance && hasAddon.value) {
        dateRows.push({
          label: t('subscription.info.finalRecap.examInsurance'),
          value: t('subscription.info.finalRecap.examOffered'),
        })
      }
    }

    // Monthly is offered only for duration covers lasting at least 2 months.
    const monthlyAvailable =
      policy.showPaymentSelector &&
      policy.priceMode === 'duration' &&
      (duration.value ?? 0) >= MIN_MONTHS_FOR_MONTHLY

    return {
      cards,
      dateRows,
      showPaymentSelector: policy.showPaymentSelector,
      monthlyAvailable,
      showClub: policy.showClub,
      clubImage: CLUB_IMAGE,
      priceMode: policy.priceMode,
      stayText: stayTotalText.value,
      durationCount: duration.value,
      total: subscriptionTotal.value,
      totalMonthly: monthlyTotal.value,
    }
  })

  return {
    // Product flags
    isHealthExpenses,
    isCivil,
    isAbroad,
    isArrivingFrance,
    isHealthTopUp,
    isRegionalAura,
    isSolidariteExam,
    ageBounds,
    // Display helpers
    formatPrice,
    formatDate,
    detailName,
    // Info sub-step rules
    assiaForwardingAutomatic,
    infoStepCount,
    infoRecapStep,
    // Offer / recap view-models
    hasAddon,
    tonicSubscribed,
    topUpName,
    endDate,
    civilEndDate,
    franceEndDate,
    abroadChosen,
    civilChosen,
    franceChosen,
    subscriptionTotal,
    monthlyTotal,
    yearlyTotal,
    stayDurationText,
    stayTotalText,
    topUpBundleName,
    regionalOfferName,
    regionalOfferImage,
    examOfferName,
    summaryCards,
    recapView,
    // Image assets
    productImage: PRODUCT_IMAGE,
    topUpImage: TOP_UP_IMAGE,
    clubImage: CLUB_IMAGE,
  }
}
